import { autoinject, bindable, bindingMode, containerless, customElement, observable } from "aurelia-framework";
import { EventAggregator } from "aurelia-event-aggregator";

import nameof from "../../../../../common/nameof";
import { HopeCenterService } from "../../../../../services/hope-center-service";
import { ToastrService } from "../../../../../services/toastr-service";
import { NoteDataManager } from "../../../note-data-manager";
import { IHopeAdmissionFormSectionTableDetails } from "../../../../../interfaces/i-hope-center";
import { EnumsService } from "../../../../../services/enums-service";
import { EnumMap } from "../../../../../common/utilities/enum-map";
import { IEnumResponse } from "../../../../../interfaces/i-enum";
import { ILookupAndCopyBehavior } from "../../../../../interfaces/form-builder/i-behavior";
import { NoteEventsEnum } from "../../../../../enums/note-events-enum";

@autoinject
@containerless
@customElement("hope-admission-section-table")
export class HopeAdmissionSectionTable {
    @bindable({ defaultBindingMode: bindingMode.toView })
    public patientId: string;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public lookupAndCopyBehavior: ILookupAndCopyBehavior;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public hasViewPermission: boolean;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public hasEditPermission: boolean;
    @observable({
        changeHandler: nameof<HopeAdmissionSectionTable>("sortChanged")
    })
    public sortBy: string = "taskDate";
    @observable({
        changeHandler: nameof<HopeAdmissionSectionTable>("sortChanged")
    })
    public sortOrder: string = "asc";
    private readonly _hopeCenterService: HopeCenterService;
    private readonly _enumsService: EnumsService;
    private readonly _toastrService: ToastrService;
    private readonly _ea: EventAggregator;
    public noteDataManager: NoteDataManager;
    public columns: number = 5;
    public isLoading: boolean = false;
    public hasLoadingError: boolean = false;
    public sectionTableData: IHopeAdmissionFormSectionTableDetails[] = [];
    public hopeCodeSectionEnum: IEnumResponse[] = [];
    public hopeCodeSectionEnumMap: EnumMap = new EnumMap([]);
    public scheduleStatusEnum: IEnumResponse[] = [];
    public scheduleStatusEnumMap: EnumMap = new EnumMap([]);
    public noteId: string = "";
    public sortByOptions: Map<string, string> = new Map([
        [nameof<IHopeAdmissionFormSectionTableDetails>("task"), "Task"],
        [nameof<IHopeAdmissionFormSectionTableDetails>("taskDate"), "Date"],
        [nameof<IHopeAdmissionFormSectionTableDetails>("taskStatus"), "Status"],
        [nameof<IHopeAdmissionFormSectionTableDetails>("assignedTo"), "Assigned To"]
    ]);

    public constructor(
        hopeCenterService: HopeCenterService,
        enumsService: EnumsService,
        toastrService: ToastrService,
        noteDataManager: NoteDataManager,
        ea: EventAggregator
    ) {
        this._hopeCenterService = hopeCenterService;
        this._enumsService = enumsService;
        this._toastrService = toastrService;
        this._ea = ea;
        this.noteDataManager = noteDataManager;
    }

    public async attached() {
        this.hopeCodeSectionEnum = await this._enumsService.getHopeCodeSectionEnum();
        this.hopeCodeSectionEnumMap = new EnumMap(this.hopeCodeSectionEnum);
        this.scheduleStatusEnum = await this._enumsService.getScheduleStatus();
        this.scheduleStatusEnumMap = new EnumMap(this.scheduleStatusEnum);
        this.isLoading = true;
        this.noteId = this.noteDataManager.getNoteId();
        this.patientId = this.noteDataManager.getPatientId();
        this.getTableInfoForHopeAdmissionForm();
    }

    public async getTableInfoForHopeAdmissionForm() {
        try {
            this.isLoading = true;
            if (!!this.lookupAndCopyBehavior) {
                let sectionCode = this.hopeCodeSectionEnumMap.getEnumValue(
                    this.lookupAndCopyBehavior.component as string
                );
                let query = {
                    hopeExportId: this.noteId,
                    patientId: this.patientId,
                    section: sectionCode,
                    order: this.sortOrder,
                    sortBy: this.sortBy
                };
                this.sectionTableData =
                    (await this._hopeCenterService.getHopeTableInfoForHopeAdmissionForm(this.noteId, query)) ?? [];
                this.sectionTableData.forEach((item) => {
                    if (
                        item.rootCodes.length > 0 &&
                        item.rootCodes.some((element) => {
                            return element.isSelected;
                        })
                    ) {
                        item.showRow = true;
                    } else {
                        item.showRow = false;
                    }
                });
            }
            this.isLoading = false;
            this.hasLoadingError = false;
        } catch (e) {
            console.error(e);
            this.hasLoadingError = true;
            this._toastrService.error({
                title: "Error: data fetching issue",
                message: "There was an error while fetching the patient's Hope Admission form data."
            });
        } finally {
            this.isLoading = false;
        }
    }

    public async replaceAnswer(sourceIds: string[]) {
        try {
            let sectionCode = this.hopeCodeSectionEnumMap.getEnumValue(this.lookupAndCopyBehavior.component as string);
            let query = {
                hopeExportId: this.noteId,
                patientId: this.patientId,
                section: sectionCode,
                hopeCodeSourceIds: sourceIds
            };
            await this._hopeCenterService.replaceAnswerHopeTableInfoSectionsRowInAdmissionForm(this.noteId, query);

            this._ea.publish(NoteEventsEnum.Refresh, this.noteId);
        } catch (e) {
            console.error(e);
        }
    }

    public changeRowVisibility(index: number) {
        this.sectionTableData[index].showRow = !this.sectionTableData[index]?.showRow;
    }

    public async sortChanged() {
        if (!!this.sectionTableData) {
            await this.getTableInfoForHopeAdmissionForm();
        }
    }
}
